import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { StyleSheet, Pressable, Animated, View, Platform, Alert, PermissionsAndroid } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { requestRecordingPermissionsAsync } from 'expo-audio';
import Icon from '@/components/Icon';
import Tooltip from '@/components/Tooltip';
import { useTheme } from '@/context/ThemeContext';
import { useToastStore } from '@/store';
import dictateModeService, { DICTATE_STATES, DICTATE_NOTICES } from '@/lib/dictateMode';
import useTranslation from '@/hooks/useTranslation';
import useRequireAuth from '@/hooks/useRequireAuth';
import { lightTap, mediumTap, notifyError } from '@/constants/utils/haptics';

const BUTTON_SIZE = 38;
const INNER_SIZE = 32;

const styles = StyleSheet.create({
  button: {
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    borderRadius: BUTTON_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'relative',
  },
  buttonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  innerContainer: {
    width: INNER_SIZE,
    height: INNER_SIZE,
    borderRadius: INNER_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && { transition: 'background-color 0.2s ease' }),
  },
});

function DictateModeButton({ 
  isDisabled = false,
  onTranscript,
  onError,
  onDictateStateChange,
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const requireAuth = useRequireAuth();
  const isFocused = useIsFocused();
  const { addToast } = useToastStore(state => state.actions);
  const [dictateState, setDictateState] = useState(dictateModeService.state || DICTATE_STATES.IDLE);
  const ownerRef = useRef(null);
  if (ownerRef.current === null) {
    ownerRef.current = Symbol('dictate-button');
  }
  const onDictateStateChangeRef = useRef(onDictateStateChange);
  useEffect(() => { onDictateStateChangeRef.current = onDictateStateChange; }, [onDictateStateChange]);
  
  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);
  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // Surface errors where the user will actually see them: a toast on web
  // (there is no system alert), the native alert elsewhere.
  const reportError = useCallback((message) => {
    notifyError();
    if (onErrorRef.current) {
      onErrorRef.current(message);
      return;
    }
    console.error('[DictateModeButton] Error:', message);
    if (Platform.OS === 'web') {
      addToast(message, 'error');
    } else {
      Alert.alert(t('chatInput.dictate.errorTitle'), message);
    }
  }, [addToast, t]);

  const sessionCallbacks = useMemo(() => ({
    onStateChange: null,
    onTranscript: (text) => {
      lightTap();
      onTranscriptRef.current?.(text);
    },
    onError: reportError,
    onNotice: (notice) => {
      const message = notice === DICTATE_NOTICES.TOO_SHORT
        ? t('chatInput.dictate.tooShort')
        : t('chatInput.dictate.noSpeech');
      addToast(message, 'default');
    },
  }), [t, addToast, reportError]);
  
  const [isHovered, setIsHovered] = useState(false);
  const hoverScaleAnim = useRef(new Animated.Value(1)).current;

  // Only the button that starts a session owns the singleton callbacks.
  // iOS keeps back-stack screens mounted, so configuring on mount lets a
  // hidden composer steal state/transcripts from the visible one.
  useEffect(() => {
    const owner = ownerRef.current;
    let previous = dictateModeService.state;
    const unsubscribe = dictateModeService.subscribeState((state) => {
      // Confirm the mic is live with a tap the moment recording begins. Only
      // the owning button taps — every mounted composer sees this state.
      if (
        state === DICTATE_STATES.RECORDING
        && previous !== DICTATE_STATES.RECORDING
        && dictateModeService.owner === owner
      ) {
        mediumTap();
      }
      previous = state;
      setDictateState(state);
      onDictateStateChangeRef.current?.(state);
    });
    return () => {
      unsubscribe();
      dictateModeService.release(owner);
    };
  }, []);

  // When navigation reveals a composer that was already mounted in the
  // back-stack, transfer the active singleton session to that visible input.
  // Its overlay can then stop the recording and receive the transcript.
  useEffect(() => {
    if (isFocused && dictateModeService.state !== DICTATE_STATES.IDLE) {
      dictateModeService.adopt(ownerRef.current, sessionCallbacks);
    }
  }, [isFocused, sessionCallbacks]);

  const isStarting = dictateState === DICTATE_STATES.STARTING;
  const isRecording = dictateState === DICTATE_STATES.RECORDING;
  const isProcessing = dictateState === DICTATE_STATES.PROCESSING;
  const isActive = isStarting || isRecording || isProcessing;

  const handlePress = useCallback(async () => {
    if (isDisabled || isStarting || isProcessing) return;

    // Dictation calls a paid backend transcription endpoint — anonymous
    // visitors need an account first.
    const gated = requireAuth(() => true, {
      source: 'composer_dictate',
      title: t('chatInput.authCta.dictateTitle'),
      subtitle: t('chatInput.authCta.dictateSubtitle'),
      icon: 'microphone-lines',
    });
    if (gated !== true) return;

    if (Platform.OS === 'android') {
      try {
        // expo-av's permission helper does not reliably display the Android
        // system prompt in production builds. Request RECORD_AUDIO directly,
        // matching the proven Voice Mode permission path.
        const granted = await PermissionsAndroid.request(
          PermissionsAndroid.PERMISSIONS.RECORD_AUDIO,
          {
            title: t('chatInput.dictate.microphoneRequiredTitle'),
            message: t('chatInput.dictate.microphoneMessage'),
            buttonNegative: t('chatInput.voice.deny'),
            buttonPositive: t('chatInput.voice.allow'),
          }
        );
        if (granted !== PermissionsAndroid.RESULTS.GRANTED) {
          Alert.alert(
            t('chatInput.dictate.microphoneRequiredTitle'),
            t('chatInput.dictate.microphoneMessage'),
            [
              { text: t('common.cancel'), style: 'cancel' },
              {
                text: t('chatInput.dictate.openSettings'),
                onPress: () => {
                  try { require('react-native').Linking.openSettings(); } catch (_) {}
                },
              },
            ]
          );
          return;
        }
        // The permission dialog briefly backgrounds the activity on some
        // Android devices. Give the audio service time to become active again.
        await new Promise(resolve => setTimeout(resolve, 400));
      } catch (err) {
        console.error('[DictateModeButton] Android permission request failed:', err);
        Alert.alert(t('chatInput.dictate.errorTitle'), t('chatInput.dictate.startFailed'));
        return;
      }
    } else if (Platform.OS === 'ios') {
      try {
        const { status } = await requestRecordingPermissionsAsync();
        if (status !== 'granted') {
          Alert.alert(
            t('chatInput.dictate.microphoneRequiredTitle'),
            t('chatInput.dictate.microphoneMessage'),
            [
              { text: t('common.cancel'), style: 'cancel' },
              { text: t('chatInput.dictate.openSettings'), onPress: () => {
                try { require('react-native').Linking.openSettings(); } catch (_) {}
              }},
            ]
          );
          return;
        }
      } catch (err) {
        console.error('[DictateModeButton] Permission request failed:', err);
        return;
      }
    }

    try {
      await dictateModeService.toggle({
        owner: ownerRef.current,
        callbacks: sessionCallbacks,
        skipPermissionCheck: Platform.OS === 'ios' || Platform.OS === 'android' || Platform.OS === 'macos',
      });
    } catch (err) {
      console.error('[DictateModeButton] Toggle failed:', err);
      reportError(err?.message || t('chatInput.dictate.startFailed'));
    }
  }, [isDisabled, isStarting, isProcessing, requireAuth, sessionCallbacks, t, reportError]);

  const handleHoverIn = useCallback(() => {
    setIsHovered(true);
    Animated.spring(hoverScaleAnim, {
      toValue: 1.05,
      friction: 10,
      tension: 200,
      useNativeDriver: true,
    }).start();
  }, [hoverScaleAnim]);

  const handleHoverOut = useCallback(() => {
    setIsHovered(false);
    Animated.spring(hoverScaleAnim, {
      toValue: 1,
      friction: 10,
      tension: 200,
      useNativeDriver: true,
    }).start();
  }, [hoverScaleAnim]);

  // When recording or processing, the parent shows the full overlay instead
  if (isActive) return null;

  return (
    <Tooltip label={t('chatInput.dictate.dictate')}>
      <Animated.View style={{ transform: [{ scale: hoverScaleAnim }] }}>
        <Pressable 
          style={[
            styles.button,
            isDisabled && styles.buttonDisabled,
          ]}
          onPress={handlePress}
          onHoverIn={handleHoverIn}
          onHoverOut={handleHoverOut}
          disabled={isDisabled}
          accessibilityLabel={t('chatInput.dictate.dictate')}
          accessibilityRole="button"
          accessibilityState={{ disabled: isDisabled }}
        >
          <View style={[
            styles.innerContainer,
            !isDisabled && isHovered && { backgroundColor: colors.solid_color_tertiary },
          ]}>
            <Icon 
              name="microphone-lines"
              solid
              size={14}
              color={colors.text_color_tertiary}
            />
          </View>
        </Pressable>
      </Animated.View>
    </Tooltip>
  );
}

export default React.memo(DictateModeButton);
